import { unparse } from 'papaparse';
import * as XLSX from 'xlsx';

import { formatDate } from '@common/utils/dates';

/**
 * RFC 4180 single-field escaper. Returns '' for null/undefined; wraps a value in double quotes
 * (doubling any embedded quote) only when it contains a comma, double-quote, CR or LF — so
 * comma-free legacy values are returned byte-identical.
 *
 * Also neutralizes spreadsheet formula injection: a value beginning with =, +, -, @, tab or CR is
 * treated as a formula by Excel/Sheets, so we prefix it with a single quote (which those apps render
 * as literal text — and RFC-4180 quoting alone does NOT prevent this, as the quotes are stripped on
 * import). Exported free-text/URL columns (e.g. campaign destination URLs) are user-controlled, so
 * without this a crafted value like `=cmd|'/c ...'!A1` would execute when an admin opens the export.
 */
export const csvField = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  let str = String(value);

  if (/^[=+\-@\t\r]/.test(str)) {
    str = `'${str}`;
  }

  return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

export const downloadFile = (data, filename = 'download') => {
  const file = new Blob([data], { type: 'text/csv' });
  const url = window.URL.createObjectURL(file);
  const a = document.createElement('a');

  a.download = `${filename}.csv`;
  a.href = url;
  a.style.display = 'none';

  document.body.appendChild(a);

  a.click();

  document.body.removeChild(a);
};

export const exportDataToCSV = (data, filename = 'export') => {
  const text = unparse(data);
  const date = formatDate(new Date(), 'MM-dd-yyyy');

  downloadFile(text, `${filename}-${date}`);
};

export const exportTableToCSV = (node, filename) => {
  const data = [];
  const rows = node.getElementsByTagName('tr');

  for (let i = 0, k = rows.length; i < k; i++) {
    const columns = rows[i].querySelectorAll('td,th');
    const cells = [];

    for (let j = 0, l = columns.length; j < l; j++) {
      cells.push(columns[j].innerText.replace(/(?:\r\n|\r|\n)/g, ''));
    }

    data.push(cells.join(','));
  }

  downloadFile(data.join('\n'), filename);
};

export async function readFile(file) {
  const reader = new FileReader();

  return new Promise((resolve, reject) => {
    if (file.type === 'text/csv') {
      reader.onload = (e) => {
        if (e.target) {
          resolve(e.target.result);
        } else {
          reject('Error reading CSV file');
        }
      };

      reader.onerror = () => reject('Error reading CSV file');
      reader.readAsText(file);
    } else if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      reader.onload = (e) => {
        if (e.target) {
          const workbook = XLSX.read(e.target.result);
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const csvText = XLSX.utils.sheet_to_csv(worksheet);
          resolve(csvText);
        } else {
          reject('Error reading Excel file');
        }
      };

      reader.onerror = () => reject('Error reading Excel file');
      reader.readAsArrayBuffer(file);
    } else {
      reject('Unsupported file type');
    }
  });
}
